import { Box, Button, Card, CardContent, Stack, Typography } from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAccount } from "src/contexts/accountContext";

export default function ApprovedIdPurchaseGate({ children }) {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const account = useAccount();
  const isAuthenticated = Boolean(account?.isLoggedIn || account?.isQuickLoggedIn || account?.sessionAuth);
  const state = String(account?.legalIdState || "").trim().toLowerCase();
  let storedLegalId = "";
  try {
    storedLegalId = window.localStorage.getItem("agent-legal-id") || window.sessionStorage.getItem("agent-legal-id") || "";
  } catch { /* storage may be unavailable */ }
  const hasLegalId = Boolean(account?.legalId || storedLegalId);
  const isApproved = isAuthenticated && state === "approved" && hasLegalId;
  const isPending = state !== "identityrequired" && (
    account?.legalStatus?.pendingApplication === true
    || ["created", "pending", "under review", "in review"].includes(state)
  );
  const needsIdentityApplication = state === "identityrequired" || (!isPending && isAuthenticated && !isApproved);

  if (isApproved) return children;

  const continueToLogin = () => {
    try { window.localStorage.setItem("pendingCheckout", "true"); } catch { /* ignore */ }
    navigate("/auth");
  };

  return (
    <Box sx={{ minHeight: "72vh", display: "grid", placeItems: "center", px: 2, py: { xs: 5, md: 8 }, bgcolor: "#F5F7F8" }}>
      <Card variant="outlined" sx={{ width: "100%", maxWidth: 600, borderRadius: 4, borderColor: "#E3E9E8", bgcolor: "#FFFFFF", boxShadow: "0 20px 54px rgba(18, 45, 39, 0.09)" }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 }, "&:last-child": { pb: { xs: 3, sm: 4 } } }}>
          <Stack spacing={2.5} alignItems="center" textAlign="center">
            <Box sx={{ width: 64, height: 64, display: "grid", placeItems: "center", borderRadius: 3, bgcolor: "#E8F4F0", color: "#087B65" }}>
              <BadgeOutlinedIcon sx={{ fontSize: 34 }} />
            </Box>
            <Box>
              <Typography variant="overline" sx={{ fontWeight: 750, color: "#087B65", letterSpacing: "0.12em" }}>
                {t("purchaseApproval.eyebrow", "PURCHASE ELIGIBILITY")}
              </Typography>
              <Typography variant="h5" sx={{ mt: 0.25, fontWeight: 800, color: "#17212F", letterSpacing: "-0.02em" }}>
                {isPending
                  ? t("purchaseApproval.pendingTitle", "Your personal ID application is being reviewed")
                  : needsIdentityApplication
                    ? t("purchaseApproval.applicationTitle", "Apply for a personal ID to purchase")
                    : t("purchaseApproval.requiredTitle", "An approved personal ID is required to purchase")}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: "#526174", lineHeight: 1.7 }}>
                {isPending
                  ? t("purchaseApproval.pendingMessage", "You can keep browsing and adding items to your cart. Checkout will be available once your personal ID is approved.")
                  : needsIdentityApplication
                    ? t("purchaseApproval.applicationMessage", "A Neuro Access account by itself is not enough to purchase. If you have already applied for a personal ID, please wait for approval. If not, open Neuro Access and tap the Apply for a personal ID banner on the home screen shown below.")
                    : t("purchaseApproval.requiredMessage", "You can keep browsing and adding items to your cart. To continue to payment, sign in with Neuro Access and make sure your personal ID is approved.")}
              </Typography>
            </Box>

            {!isPending && needsIdentityApplication && (
              <Box sx={{ width: "100%", p: { xs: 2, sm: 2.25 }, borderRadius: 3, bgcolor: "#EEF5F8", border: "1px solid #DCE9EE", textAlign: "left" }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
                  <Box sx={{ flex: 1 }}>
                    <Stack direction="row" spacing={1.1} alignItems="flex-start">
                      <InfoOutlinedIcon sx={{ mt: 0.15, color: "#346783" }} />
                      <Box>
                        <Typography sx={{ fontWeight: 750, color: "#243B4A" }}>
                          {t("purchaseApproval.applicationPromptTitle", "Apply from the Neuro Access home screen")}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5, color: "#4D6573", lineHeight: 1.6 }}>
                          {t("purchaseApproval.applicationSteps", "Open Neuro Access and tap the “Apply for a personal ID” banner on the home screen, like the example.")}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box
                    component="img"
                    src={`${process.env.PUBLIC_URL}/images/neuro-access-apply-personal-id.jpeg`}
                    alt={t("purchaseApproval.applicationScreenshotAlt", "Neuro Access home screen showing the Apply for a personal ID banner")}
                    sx={{ width: { xs: 150, sm: 138 }, maxHeight: { xs: 322, sm: 296 }, objectFit: "contain", borderRadius: 2, flexShrink: 0, boxShadow: "0 5px 16px rgba(20, 50, 64, 0.12)" }}
                  />
                </Stack>
              </Box>
            )}

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} sx={{ width: "100%", pt: 0.25 }}>
              {!isAuthenticated && (
                <Button variant="outlined" onClick={continueToLogin} sx={{ flex: 1, minHeight: 48, borderRadius: 2.5, borderColor: "#C9D6D3", color: "#273D39" }}>
                  {t("purchaseApproval.signIn", "Sign in with Neuro Access")}
                </Button>
              )}
              {isAuthenticated && !isPending && (
                <Button variant="outlined" onClick={continueToLogin} sx={{ minHeight: 48, borderRadius: 2.5, borderColor: "#C9D6D3", color: "#273D39" }}>
                  {t("purchaseApproval.refreshStatus", "Already approved? Sign in to refresh")}
                </Button>
              )}
            </Stack>
            <Typography variant="caption" sx={{ color: "#718096", maxWidth: 440, lineHeight: 1.55 }}>
              {isPending
                ? t("purchaseApproval.pendingHelp", "We’ll let you continue checkout after your personal ID is approved.")
                : t("purchaseApproval.statusHelp", "After approval, sign in again to refresh your status and continue your purchase.")}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
